import Sugar from 'sugar';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/input/input.js';
import '@awesome.me/webawesome/dist/components/select/select.js';
import '@awesome.me/webawesome/dist/components/option/option.js';
import '@awesome.me/webawesome/dist/components/switch/switch.js';
import type WaDialog from '@awesome.me/webawesome/dist/components/dialog/dialog.js';
import type WaInput from '@awesome.me/webawesome/dist/components/input/input.js';
import type WaSelect from '@awesome.me/webawesome/dist/components/select/select.js';
import type WaSwitch from '@awesome.me/webawesome/dist/components/switch/switch.js';

import { nsButtonStyles, nsLayoutStyles } from '../ns-styles/index.js';
import { toDateInputValue } from '../helpers/format.js';

export enum DialogInputType {
  TEXT,
  NUMBER,
  TOGGLE,
  SELECT,
  DATE,
}

interface SelectOption {
  value: string;
  label: string;
}
interface DialogSchemaInput {
  type: DialogInputType;
  label: string;
  name: string;
  value: string;
  default?: string;
  disabled?: boolean;
  classes: { [index: string]: boolean };
  options?: SelectOption[];
  // A text input's limit and the help under it
  maxlength?: number;
  hint?: string;
}
interface DialogSchemaSection {
  key: string;
  title: string;
  inputs: DialogSchemaInput[];
}
export interface DialogSchema {
  title?: string;
  description?: string;
  sections: DialogSchemaSection[];
  values?: { [index: string]: { [index: string]: string } };
}

interface DialogMetadata {
  title?: string;
  description?: string;
  actionLabel?: string;
}

interface DialogInputSchemaInput {
  type: DialogInputType;
  label: string;
  value?: string;
  default?: string;
  disabled?: boolean;
  options?: SelectOption[];
}
export interface DialogInputSchema {
  sections: {
    [key: string]: {
      title: string;
      fields: { [key: string]: DialogInputSchemaInput };
    };
  };
}

export class ErrorDialogClosed extends Error {
  schema: DialogSchema;

  constructor(schema: DialogSchema) {
    super('Dialog closed early');
    this.schema = schema;
  }
}

@customElement('ltn-dialog-service')
export class LtnDialogService extends LitElement {
  @query('#dialog')
  private __dialog?: WaDialog;

  private __schema?: DialogSchema;

  @state()
  private __metadata?: DialogMetadata;

  @state()
  private __section?: DialogSchemaSection;

  @state()
  private __isSingleSection: boolean = false;

  private __promiseHandler?: {
    resolve: (schema: DialogSchema) => void;
    reject: (err: ErrorDialogClosed) => void;
  };

  @state()
  private __noEdit: boolean = false;

  private __onChange?: Function;

  private __submitted?: boolean = false;

  static styles = [
    nsLayoutStyles,
    nsButtonStyles,
    css`
      :host {
        display: block;
      }

      wa-dialog {
        --width: 35rem;
      }

      .description {
        margin: 0 0 var(--wa-space-m);

        color: var(--ns-theme-grey-dark);
      }

      .section-nav {
        margin-bottom: var(--wa-space-xs);
      }

      .chevron {
        font-family: 'Material Icons';
        font-size: var(--wa-font-size-xl);
        line-height: 1;
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      wa-input,
      wa-select,
      wa-switch {
        display: block;

        width: 100%;
        margin-bottom: var(--wa-space-s);
      }

      wa-select {
        --wa-form-control-activated-color: var(--wa-color-brand-fill-loud);
      }
    `,
  ];

  open(
    metadata: DialogMetadata,
    dialogSchema: DialogSchema,
    opts?: { openSectionIdx?: number; noEdit?: boolean; onChange?: Function },
  ): Promise<DialogSchema> {
    this.setSchema(dialogSchema);

    if (!metadata.actionLabel) {
      metadata.actionLabel = 'Submit';
    }

    this.__metadata = metadata;

    const openSectionIdx =
      opts?.openSectionIdx && this.__schema?.sections[opts.openSectionIdx] ? opts.openSectionIdx : 0;

    if (this.__schema?.sections) {
      this.setSection(this.__schema.sections[openSectionIdx]);
    }

    if (opts?.noEdit !== undefined) {
      this.__noEdit = opts?.noEdit;
    }

    this.__onChange = opts?.onChange !== undefined ? opts?.onChange : undefined;

    this.__submitted = false;

    if (this.__dialog) this.__dialog.open = true;

    return new Promise((resolve, reject) => {
      this.__promiseHandler = { resolve, reject };
    });
  }

  setSchema(dialogSchema: DialogSchema) {
    this.__schema = dialogSchema;

    this.__isSingleSection = this.__schema.sections.length < 2;
  }

  setSection(section: DialogSchemaSection) {
    this.__section = section;
  }

  static parseInputSchema(inputSchema: DialogInputSchema, sectionValues?: any) {
    const schemaSections: DialogSchemaSection[] = [];
    const output: DialogSchema = {
      sections: schemaSections,
      values: {},
    };

    Object.keys(inputSchema.sections).forEach((sectionKey) => {
      const section = inputSchema.sections[sectionKey];
      const inputs = Object.keys(section.fields).map((key) => {
        const input: DialogSchemaInput = {
          type: section.fields[key].type,
          label: section.fields[key].label,
          name: key,
          value: section.fields[key].value || '',
          default: section.fields[key].default,
          disabled: section.fields[key].disabled,
          options: section.fields[key].options || [],
          classes: {},
        };

        let value = null;

        switch (input.type) {
          case DialogInputType.TEXT:
          case DialogInputType.NUMBER:
            value = input.value ? input.value : '';
            break;
          case DialogInputType.TOGGLE:
            value = false;
            break;
          case DialogInputType.SELECT:
            value = null;
            break;
          case DialogInputType.DATE:
            value = Sugar.Date.create('now');
            break;
          default:
        }

        if (sectionValues && sectionValues[sectionKey] && sectionValues[sectionKey][key]) {
          if (sectionValues[sectionKey][key] !== undefined && sectionValues[sectionKey][key] !== null) {
            value = sectionValues[sectionKey][key];
          }
        } else if (input.default && input.type === DialogInputType.DATE) {
          value = Sugar.Date.create(input.default);
        } else if (input.default) {
          value = input.default;
        }

        if (input.type === DialogInputType.TOGGLE) {
          value = value ? true : false;
        }

        if (input.type === DialogInputType.DATE) {
          value = toDateInputValue(value);
        }

        input.name = key;
        input.value = value;

        return input;
      });

      output.sections.push({
        key: sectionKey,
        title: section.title,
        inputs,
      });
    });

    return output;
  }

  private __sectionLeft() {
    const sections = this.__schema?.sections;
    const currentSection = this.__section;
    if (!sections || !currentSection) return;

    const currentIdx = sections.findIndex((s) => s === currentSection);
    if (currentIdx === -1) return;

    const idx = currentIdx === 0 ? sections.length - 1 : currentIdx - 1;
    this.__section = sections[idx];
  }

  private __sectionRight() {
    const sections = this.__schema?.sections;
    const currentSection = this.__section;
    if (!sections || !currentSection) return;

    const currentIdx = sections.findIndex((s) => s === currentSection);
    if (currentIdx === -1) return;

    const idx = currentIdx >= sections.length - 1 ? 0 : currentIdx + 1;
    this.__section = sections[idx];
  }

  private __validate() {
    this.__retrieveValues(true);

    if (this.__promiseHandler && this.__schema) {
      this.__promiseHandler.resolve(this.__schema);
    }

    this.dispatchEvent(
      new CustomEvent('submit', {
        detail: this.__schema,
        bubbles: true,
      }),
    );

    this.__submitted = true;
    if (this.__dialog) this.__dialog.open = false;
  }

  private __cancel() {
    if (this.__dialog) this.__dialog.open = false;
  }

  private __retrieveValues(validate: boolean = false) {
    if (!this.__schema) return;

    const values: { [index: string]: { [index: string]: string } } = {};

    this.__schema.sections.forEach((section) => {
      if (section === this.__section) {
        values[section.key] = section.inputs.reduce((_values: { [index: string]: string }, input) => {
          const domElement = this.shadowRoot?.querySelector(`#${input.name}`);
          if (!domElement) throw new Error(`Trying to retrieve input values but unable to find #${input.name}`);

          const val =
            input.type === DialogInputType.TOGGLE
              ? (domElement as WaSwitch).checked
                ? 'true'
                : 'false'
              : String((domElement as WaInput | WaSelect).value ?? '');
          input.value = val;

          _values[input.name] = val;

          return _values;
        }, {});
      } else {
        if (this.__schema?.values && this.__schema?.values[section.key]) {
          values[section.key] = this.__schema?.values[section.key];
        } else {
          values[section.key] = section.inputs.reduce((_values: { [index: string]: string }, input) => {
            _values[input.name] = input.value;
            return _values;
          }, {});
        }
      }
    });

    this.__schema.values = values;

    this.requestUpdate();

    if (!validate && this.__onChange !== undefined) this.__onChange(values);
  }

  private __formChanged(ev: Event) {
    ev.preventDefault();
    this.__retrieveValues();
  }

  // Nested components (wa-select's listbox) emit their own wa-after-hide, which bubbles up to here.
  private __dialogClosed(ev: Event) {
    if (ev.target !== this.__dialog) return;

    // If we've successfully submitted the form we can skip saving.
    if (this.__submitted) return;
    this.__retrieveValues();

    if (this.__promiseHandler) {
      this.__promiseHandler.reject(new ErrorDialogClosed(this.__schema || ({} as DialogSchema)));
    }
  }

  private __renderInput(input: DialogSchemaInput) {
    switch (input.type) {
      case DialogInputType.TEXT:
      case DialogInputType.NUMBER:
      case DialogInputType.DATE:
        return html`
          <wa-input
            id=${input.name}
            name=${input.name}
            class=${classMap(input.classes)}
            label=${input.label}
            type=${input.type === DialogInputType.NUMBER
              ? 'number'
              : input.type === DialogInputType.DATE
                ? 'date'
                : 'text'}
            .value=${input.value ?? ''}
            maxlength=${ifDefined(input.maxlength)}
            hint=${ifDefined(input.hint)}
            ?disabled=${input.disabled}
          ></wa-input>
        `;
      case DialogInputType.TOGGLE:
        return html`
          <wa-switch
            id=${input.name}
            name=${input.name}
            class=${classMap(input.classes)}
            .checked=${String(input.value) === 'true'}
            ?disabled=${input.disabled}
          >
            ${input.label}
          </wa-switch>
        `;
      case DialogInputType.SELECT:
        return html`
          <wa-select
            id=${input.name}
            name=${input.name}
            class=${classMap(input.classes)}
            label=${input.label}
            .value=${input.value ?? ''}
            ?disabled=${input.disabled}
          >
            ${repeat(
              input.options ?? [],
              (option) => option.value,
              (option) => html`
                <wa-option value=${option.value}>${option.label}</wa-option>
              `,
            )}
          </wa-select>
        `;
      default:
        return nothing;
    }
  }

  render() {
    return html`
      <wa-dialog id="dialog" light-dismiss label=${this.__metadata?.title ?? ''} @wa-after-hide=${this.__dialogClosed}>
        ${this.__metadata?.description
          ? html`
              <p class="description">${this.__metadata.description}</p>
            `
          : nothing}
        ${!this.__isSingleSection
          ? html`
              <div class="section-nav layout horizontal center">
                <div class="flex">${this.__section?.title}</div>
                <wa-button appearance="plain" class="icon-button" @click=${this.__sectionLeft}>
                  <span class="chevron" aria-hidden="true">chevron_left</span>
                  <span class="visually-hidden">Previous section</span>
                </wa-button>
                <wa-button appearance="plain" class="icon-button" @click=${this.__sectionRight}>
                  <span class="chevron" aria-hidden="true">chevron_right</span>
                  <span class="visually-hidden">Next section</span>
                </wa-button>
              </div>
            `
          : nothing}
        <form id="dialog-body" @change=${this.__formChanged} @submit=${this.__formChanged}>
          ${this.__section
            ? repeat(
                this.__section.inputs,
                (input) => input.name,
                (input) => this.__renderInput(input),
              )
            : nothing}
        </form>
        ${!this.__noEdit
          ? html`
              <wa-button slot="footer" appearance="plain" @click=${this.__cancel}>Cancel</wa-button>
              <wa-button slot="footer" variant="brand" @click=${this.__validate}>
                ${this.__metadata?.actionLabel}
              </wa-button>
            `
          : nothing}
      </wa-dialog>
    `;
  }
}
